import React, { useState } from 'react'
import {
  Paper,
  IconButton,
  TextField,
  Button,
} from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import { Delete, Add, DragHandle } from '@mui/icons-material'
import Alert from '@mui/material/Alert'
import FieldEditor from './FieldEditor'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { cardHasField } from './layoutValidation'

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fieldsContainer: {
    marginTop: theme.spacing(1),
  },
  emptyCardWarning: {
    marginTop: theme.spacing(2),
  },
  cardMeta: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

const CardEditor = ({
  cards,
  setLayout,
  addCard,
  deleteCard,
  moveCard,
  addField,
  deleteField,
  moveField,
  objectType,
}) => {
  const classes = useStyles()
  const [latestFieldChange, setLatestFieldChange] = useState(null)

  const onDragEnd = (result) => {
    if (!result.destination) return

    if (result.type === 'CARD') {
      moveCard(result.source.index, result.destination.index)
    } else if (result.type.split('-')[0] === 'FIELD') {
      const cardIndex = parseInt(result.type.split('-')[1], 10)
      moveField(cardIndex, result.source.index, result.destination.index)
    }
  }

  const handleCardTitleChange = (index, value) => {
    const newCards = [...cards]
    newCards[index].title = value
    setLayout((prevLayout) => ({ ...prevLayout, cards: newCards }))
  }

  const handleCardDescriptionChange = (index, value) => {
    const newCards = [...cards]
    newCards[index].description = value
    setLayout((prevLayout) => ({ ...prevLayout, cards: newCards }))
  }

  const createNewCard = () => {
    const newCards = [...cards, { title: '', description: 'Card description', items: [] }]
    setLayout((prevLayout) => ({ ...prevLayout, cards: newCards }))
  }

  const handleFieldChanged = (updatedField) => {
    setLatestFieldChange(updatedField)
    setLayout((previousLayout) => ({
      ...previousLayout,
      cards: previousLayout.cards.map((card) => ({
        ...card,
        items: card.items.map((item) =>
          item.field_id === updatedField.id
            ? {
                ...item,
                display_label: updatedField.label,
                display_field_type: updatedField.field_type,
              }
            : item
        ),
      })),
    }))
  }

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={createNewCard}
        startIcon={<Add />}
        style={{ marginBottom: 16 }}
      >
        Add Card
      </Button>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="cards" type="CARD">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {cards.map((card, index) => (
                <Draggable key={index} draggableId={`card-${index}`} index={index}>
                  {(provided) => (
                    <Paper
                      className={classes.cardContainer}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <div className={classes.cardHeader}>
                        <div {...provided.dragHandleProps}>
                          <DragHandle />
                        </div>
                        <TextField
                          label="Card Title"
                          value={card.title}
                          onChange={(e) => handleCardTitleChange(index, e.target.value)}
                          variant="outlined"
                          size="small"
                          style={{ flexGrow: 1, marginLeft: 8, marginRight: 8 }}
                        />
                        <IconButton onClick={() => deleteCard(index)}>
                          <Delete />
                        </IconButton>
                      </div>
                      <div className={classes.cardMeta}>
                        <TextField
                          label="Card Description"
                          value={card.description || ''}
                          onChange={(e) => handleCardDescriptionChange(index, e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                        />
                      </div>
                      {!cardHasField(card) && (
                        <Alert
                          className={classes.emptyCardWarning}
                          severity='warning'
                        >
                          This card will not be saved until at least one field is added.
                        </Alert>
                      )}
                      <FieldEditor
                        cardIndex={index}
                        items={card.items}
                        addField={(cardIndex, fieldInfo) => {
                          const newCards = [...cards]
                          newCards[cardIndex].items.push({
                            item_type: 'field',
                            field_id: fieldInfo.field_id,
                            display_label: fieldInfo.display_label,
                            display_field_type: fieldInfo.display_field_type,
                          })
                          setLayout((prev) => ({ ...prev, cards: newCards }))
                        }}
                        deleteField={deleteField}
                        moveField={moveField}
                        objectType={objectType}
                        latestFieldChange={latestFieldChange}
                        onFieldChanged={handleFieldChanged}
                      />
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default CardEditor
